import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where
} from 'firebase/firestore';

export const COMMISSION_RATES = {
  retail: 0.05,
  wholesale: 0.02
};

export const calculateCommissionAmount = (orderTotal, isWholesale) => {
  const rate = isWholesale ? COMMISSION_RATES.wholesale : COMMISSION_RATES.retail;
  return Number((parseFloat(orderTotal || 0) * rate).toFixed(2));
};

export const fetchSalesUsers = async (db) => {
  const snapshot = await getDocs(
    query(collection(db, 'users'), where('role', '==', 'satisci'))
  );
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((u) => !u.disabled)
    .sort((a, b) =>
      (a.displayName || '').localeCompare(b.displayName || '', 'tr')
    );
};

export const parseCommissionDate = (val) => {
  if (!val) return null;
  if (val.toDate) return val.toDate();
  if (val instanceof Date) return Number.isNaN(val.getTime()) ? null : val;
  const parsed = new Date(val);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const getCommissionEntryDate = (entry) =>
  parseCommissionDate(entry?.orderDate) || parseCommissionDate(entry?.createdAt);

export const applyCommissionToUser = async (db, salesUserId, commissionAmount, commissionEntry) => {
  if (!salesUserId || !commissionAmount || commissionAmount <= 0) return;
  const userRef = doc(db, 'users', salesUserId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;

  const userData = userSnap.data();
  const currentBalance = parseFloat(userData.commissionBalance || 0);
  const history = Array.isArray(userData.commissionHistory) ? userData.commissionHistory : [];
  const orderDate = parseCommissionDate(commissionEntry.orderDate) || new Date();

  await updateDoc(userRef, {
    commissionBalance: Number((currentBalance + commissionAmount).toFixed(2)),
    commissionHistory: [
      ...history,
      { ...commissionEntry, amount: commissionAmount, createdAt: new Date(), orderDate }
    ]
  });
};

export const reverseCommissionFromUser = async (db, salesUserId, customerId) => {
  if (!salesUserId) return 0;
  const userRef = doc(db, 'users', salesUserId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return 0;

  const userData = userSnap.data();
  const history = Array.isArray(userData.commissionHistory) ? userData.commissionHistory : [];

  const toRemove = history.filter((e) => e.customerId === customerId || e.dealerSessionId === customerId);
  const removedAmount = toRemove.reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0);

  if (removedAmount === 0) return 0;

  const currentBalance = parseFloat(userData.commissionBalance || 0);
  const newBalance = Math.max(0, Number((currentBalance - removedAmount).toFixed(2)));
  const filteredHistory = history.filter(
    (e) => e.customerId !== customerId && e.dealerSessionId !== customerId
  );

  await updateDoc(userRef, {
    commissionBalance: newBalance,
    commissionHistory: filteredHistory
  });

  return removedAmount;
};

export const recalculateCommissionForUser = async (
  db,
  salesUserId,
  entityId,
  entityName,
  grandTotal,
  isWholesale,
  orderDate = null
) => {
  if (!salesUserId || salesUserId === 'no-selection' || !entityId) return;

  await reverseCommissionFromUser(db, salesUserId, entityId);

  const total = parseFloat(grandTotal) || 0;
  if (total <= 0) return;

  const commissionAmt = calculateCommissionAmount(total, isWholesale);
  if (commissionAmt <= 0) return;

  await applyCommissionToUser(db, salesUserId, commissionAmt, {
    type: isWholesale ? 'wholesale' : 'retail',
    ...(isWholesale
      ? { dealerSessionId: entityId, dealerName: entityName || '' }
      : { customerId: entityId, customerName: entityName || '' }),
    orderTotal: total,
    orderDate: parseCommissionDate(orderDate) || new Date()
  });
};

export const updateCommissionOrderDateForEntity = async (db, salesUserId, entityId, orderDate) => {
  if (!salesUserId || salesUserId === 'no-selection' || !entityId) return;

  const parsedOrderDate = parseCommissionDate(orderDate);
  if (!parsedOrderDate) return;

  const userRef = doc(db, 'users', salesUserId);
  const userSnap = await getDoc(userRef);
  if (!userSnap.exists()) return;

  const userData = userSnap.data();
  const history = Array.isArray(userData.commissionHistory) ? userData.commissionHistory : [];

  let changed = false;
  const updatedHistory = history.map((entry) => {
    if (entry.customerId === entityId || entry.dealerSessionId === entityId) {
      changed = true;
      return { ...entry, orderDate: parsedOrderDate };
    }
    return entry;
  });

  if (changed) {
    await updateDoc(userRef, { commissionHistory: updatedHistory });
  }
};

export const transferCommission = async (
  db,
  oldSalesUserId,
  newSalesUserId,
  entityId,
  entityName,
  grandTotal,
  isWholesale,
  orderDate = null
) => {
  // Eski satışçıdan komisyonu geri al
  if (oldSalesUserId && oldSalesUserId !== 'no-selection') {
    await reverseCommissionFromUser(db, oldSalesUserId, entityId);
  }
  // Yeni satışçıya komisyon ekle
  if (newSalesUserId && newSalesUserId !== 'no-selection') {
    await recalculateCommissionForUser(
      db,
      newSalesUserId,
      entityId,
      entityName,
      grandTotal,
      isWholesale,
      orderDate
    );
  }
};

export const groupCommissionsByPeriod = (history = []) => {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfWeek = new Date(startOfDay);
  startOfWeek.setDate(startOfDay.getDate() - startOfDay.getDay());
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const daily = history.filter((e) => {
    const d = getCommissionEntryDate(e);
    return d && d >= startOfDay;
  });
  const weekly = history.filter((e) => {
    const d = getCommissionEntryDate(e);
    return d && d >= startOfWeek;
  });
  const monthly = history.filter((e) => {
    const d = getCommissionEntryDate(e);
    return d && d >= startOfMonth;
  });

  const sum = (arr) => arr.reduce((acc, e) => acc + (parseFloat(e.amount) || 0), 0);

  return {
    daily: { entries: daily, total: Number(sum(daily).toFixed(2)) },
    weekly: { entries: weekly, total: Number(sum(weekly).toFixed(2)) },
    monthly: { entries: monthly, total: Number(sum(monthly).toFixed(2)) },
    allTime: { entries: history, total: Number(sum(history).toFixed(2)) }
  };
};

export const buildMonthlyCommissionTrend = (history = [], year) => {
  const months = Array.from({ length: 12 }, (_, i) => i);
  const result = months.map((m) => ({ monthIndex: m, total: 0 }));

  history.forEach((e) => {
    const d = getCommissionEntryDate(e);
    if (!d) return;
    if (d.getFullYear() !== year) return;
    const m = d.getMonth();
    result[m].total = Number((result[m].total + (parseFloat(e.amount) || 0)).toFixed(2));
  });

  return result;
};
